<template>
    <div class="nlu-template">
        <ModelHeader>
            <template #actions>
              <Search
                    v-show="showSearch"
                    class="search"
                    :value="searchText"
                    @input="searchInTable"
                />
                <VersionStatus class="vesrion-status" />
                <TrainTestModelButton />
                <MigrateButton v-if="migrationIsRequired" />
                <PublishButton v-else />
            </template>
            <template #footer>
                <div class="tabs">
                    <div
                        class="tab"
                        :class="{active: activeTab === tabs.table}"
                        @click="activeTab = tabs.table"
                    >
                      {{ $t('model.nlu-model.nlu-model.table') }}
                    </div>
                    <div
                        class="tab"
                        :class="{active: activeTab === tabs.suggested}"
                        @click="activeTab = tabs.suggested"
                    >
                        {{suggestedTabLabel}}
                    </div>
                    <div
                        class="tab"
                        :class="{active: activeTab === tabs.unrecognized}"
                        @click="activeTab = tabs.unrecognized"
                    >
                        {{unrecognizedTabLabel}}
                    </div>
                    <div
                        class="tab"
                        :class="{active: activeTab === tabs.prebuiltModels}"
                        @click="activeTab = tabs.prebuiltModels"
                    >
                      {{ $t('model.nlu-model.nlu-model.prebuiltDomains') }}
                    </div>
                    <div
                        class="tab"
                        :class="{active: activeTab === tabs.settings}"
                        @click="activeTab = tabs.settings"
                    >
                      {{ $t('model.nlu-model.nlu-model.settings') }}
                    </div>
                </div>
            </template>
        </ModelHeader>
        <ModelBody>
            <BigPreloader
                v-if="isVersionPublishing"
                :caption="$t('model.nlu-model.nlu-model.preloader.caption')"
                :info="$t('model.nlu-model.nlu-model.preloader.info')"
            />
            <TableHome
                v-else-if="activeTab === tabs.table"
                :searchText="searchText"
            />
            <Settings
                v-else-if="activeTab === tabs.settings"
            />
            <PrebuiltModels
                v-else-if="activeTab === tabs.prebuiltModels"
            />
            <AutogeneratedTable
                v-else
                :autogenerated-type="activeTab"
                :searchText="searchText"
            />
        </ModelBody>
        <Sidebar />
        <NotificationPopup />
        <UtteranceRecommendationsModal />
        <UtteranceAutoPredictionModal />
        <or-confirm
            v-if="current"
            class="confirm-modal"
            ref="migrateConfirm"
            :title="$t('model.nlu-model.nlu-model.migration')"
            @confirm="migrateHandler"
        >
            <p class="warning-text">
                <or-icon
                  class="warning"
                  icon="warning"
                />
                <span>{{ $t('model.nlu-model.nlu-model.thisActionWill') }}</span>
            </p>
            <or-textbox
                v-model="migrationModelNewName"
                :label="$t('model.nlu-model.nlu-model.knowledgeModelName')"
                :placeholder="$t('model.nlu-model.nlu-model.enterNewKnowledge')"
            />
            <p
              v-if="migrationModelNewName === ''"
              class="warning-text"
            >
                <or-icon
                  class="info"
                  icon="info"
                />
                <span>
                  {{ $t('model.nlu-model.nlu-model.ifYouLeft') }}
                </span>
            </p>
        </or-confirm>
    </div>
</template>

<script>
import KnowledgeBus from '@/KnowledgeBus'
import { mapState, mapGetters, mapActions } from 'vuex'
import TableHome from '@/components/table/table_home'
import AutogeneratedTable from '@/components/autogenerated/autogenerated_table'
import ModelHeader from '@/components/model/common/model_header'
import ModelBody from '@/components/model/common/model_body'
import NotificationPopup from '@/components/model/nlu-model/sidebar/notifications/NotificationPopup'
import VersionStatus from '@/components/model/nlu-model/header/status/version_status'
import MigrateButton from '@/components/model/nlu-model/header/MigrateButton'
import TrainTestModelButton from '@/components/model/nlu-model/header/TrainTestModelButton'
import PublishButton from '@/components/model/nlu-model/header/PublishButton'
import Search from '@/components/model/nlu-model/header/Search'
import Settings from './settings/settings'
import Sidebar from './sidebar/Sidebar'
import PrebuiltModels from './prebuilt-models/prebuilt-models'
import BigPreloader from '@/components/common/BigPreloader'
import UtteranceRecommendationsModal from '@/components/common/modals/utterance_recommendations_modal/utterance_recommendations_modal'
import UtteranceAutoPredictionModal from '@/components/common/modals/utterance_auto_prediction/utterances_auto_prediction_modal'

export default {
  components: {
    Sidebar,
    ModelHeader,
    ModelBody,
    NotificationPopup,
    TableHome,
    AutogeneratedTable,
    VersionStatus,
    MigrateButton,
    TrainTestModelButton,
    PublishButton,
    Search,
    Settings,
    BigPreloader,
    PrebuiltModels,
    UtteranceRecommendationsModal,
    UtteranceAutoPredictionModal
  },

  mounted () {
    this.fetchStatus()
  },

  created () {
    KnowledgeBus.$on('migrate-confirm::open', this.openMigrateConfirm)
  },

  beforeDestroy () {
    KnowledgeBus.$off('migrate-confirm::open', this.openMigrateConfirm)
  },

  data () {
    const tabs = Object.freeze({
      table: 'table',
      suggested: 'suggested',
      unrecognized: 'unrecognized',
      settings: 'settings',
      prebuiltModels: 'prebuiltModels'
    })

    return {
      searchText: '',
      activeTab: tabs.table,
      migrationModelNewName: '',
      tabs
    }
  },

  computed: {
    ...mapState('knowledgebase', ['current']),
    ...mapState('knowledgebase/versions', ['isVersionPublishing']),
    ...mapState('knowledgebase/autogenerated', ['suggested', 'unrecognized']),
    ...mapGetters('knowledgebase', ['getCurrent']),

    showSearch () {
      return this.activeTab !== 'settings'
    },

    suggestedTabLabel () {
      const count = _.get(this.suggested, 'total', null)
      return _.isNull(count) ? this.$t('model.nlu-model.nlu-model.suggested.name') : this.$t('model.nlu-model.nlu-model.suggested.withCount', { count })
    },

    unrecognizedTabLabel () {
      const count = _.get(this.unrecognized, 'total', null)
      return _.isNull(count) ? this.$t('model.nlu-model.nlu-model.unrecognized.name') : this.$t('model.nlu-model.nlu-model.unrecognized.withCount', { count })
    },

    migrationIsRequired () {
      return this.getCurrent ? this.getCurrent.isOld : false
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['migrateKb', 'fetchCategories']),
    ...mapActions('knowledgebase/autogenerated', ['fetchAutogenerated']),
    ...mapActions('knowledgebase/statuses', ['fetchStatus']),

    openMigrateConfirm () {
      this.$refs.migrateConfirm.open()
    },

    migrateHandler () {
      this.$refs.migrateConfirm.close()
      this.migrateKb(this.migrationModelNewName)
    },

    searchInTable (value) {
      if ((this.searchText !== value) && (value.length > 1 || _.isEmpty(value))) {
        this.searchText = value

        this.activeTab === 'table'
          ? this.fetchCategories(value)
          : this.fetchAutogenerated({ type: this.activeTab, searchText: value })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
    ::v-deep .model-body-wrap {
        /* default slider width */
        padding-right: $sp-5;
    }

    .nlu-template {
        position: relative;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        overflow: hidden;
        background-color: $or-grey-light;
    }

    .search {
      margin-right: auto;
    }

    .vesrion-status {
      margin-right: 8px;
    }

    .confirm-modal {
        .warning-text {
            display: flex;
            margin-top: $sp-3;

            p {
                display: flex;
                align-items: center;
            }

            .material-icons {
                font-size: 18px;
                margin-right: 8px;

                &.info {
                    color: $or-blue;
                }

                &.warning {
                    color: $or-yellow;
                }
            }
        }
    }
</style>
